import { ReactElement, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Yup from 'utils/validation/yup-extensions';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { Form, Formik } from 'formik';
import {
	DELEGATE_FIELD_KEYS,
	DELEGATE_FIELD_STEPS,
	DELEGATE_FIELDS,
	DELEGATE_STEPS,
} from 'pages/AnnualUserAccessReview/views/Delegate/_config';
import FixedBottomNavigation from 'pages/AnnualUserAccessReview/components/FixedBottomNavigation';
import {
	extractValuesArray,
	extractValuesObject,
	hasErrors,
	isStepError,
	navigate,
	processStaffDataForTable,
} from 'pages/AnnualUserAccessReview/_util';
import SelectUsers from 'pages/AnnualUserAccessReview/components/SelectUsers';
import ReviewAndSubmit from './ReviewAndSubmit';
import SelectDates from './SelectDates';
import { UserContext } from 'context/User/UserContext';
import SelectDelegateeField from './SelectDelegateeField';
import { NAVIGATION_ACTIONS } from 'pages/AnnualUserAccessReview/_const';
import {
	fetchBasicStaffList,
	submitDelegation,
} from 'pages/AnnualUserAccessReview/_api';
import withQueryCheck from '../../components/withQueryCheck';
import { AnnualReviewDelegationPayload } from 'pages/AnnualUserAccessReview/_classes';
import WithLoadingHandler from 'hoc/withLoadingHandler/withLoadingHandler';

/**
 * Form to submit when manager wishes to grant review privileges to another direct report
 * @param {object[]} initialValuesFromParams user information for the target users from the query parameters; received from `withQueryCheck` HOC
 * @param {UserContext} loggedInUser the currently logged in user; received from `withQueryCheck` HOC
 * @param {UserContext} targetUser the target manager to load information for; received from `withQueryCheck` HOC
 * @param {object[]} messages special messages to display on screen; received from `withQueryCheck` HOC
 * @param {Boolean} open indicates if help panel is open
 * @returns {ReactElement} User Access Review Employee Delegate Form
 */
const DelegateForm = ({
	initialValuesFromParams,
	loggedInUser,
	targetUser,
	messages = [],
	open = false,
}) => {
	const [sActiveStep, setActiveStep] = useState(0);
	const [sStepProgress, setStepProgress] = useState(0);
	const [sSubmissionStatus, setSubmissionStatus] = useState(null);

	const injectFromParams = obj => {
		if (initialValuesFromParams.length > 0) {
			obj[DELEGATE_FIELD_KEYS.DELEGATEE] = initialValuesFromParams.map(
				o => ({
					...o,
					isSelected: true,
				})
			);
		}
		return obj;
	};

	/**
	 * watch `initialValuesFromParams` and if query params are present, transition to next step automatically
	 */
	useEffect(() => {
		let active = true;

		if (active && initialValuesFromParams.length > 0) {
			setStepProgress(1);
			setActiveStep(1);
		}

		return () => {
			active = false;
		};
	}, [initialValuesFromParams]);

	const initialValues = injectFromParams(
		extractValuesObject(DELEGATE_FIELDS, 'defaultValue')
	);
	const validationSchema = Yup.object().shape(
		extractValuesObject(DELEGATE_FIELDS, 'validationSchema')
	);
	const stepLabels = extractValuesArray(DELEGATE_STEPS);

	const onNavigate = type => {
		let newStep = navigate(type, sActiveStep, stepLabels.length);
		setStepProgress(Math.max(newStep, sStepProgress));
		setActiveStep(newStep);
	};

	const [sIsLoading, setIsLoading] = useState(true);

	const [sStaff, setStaff] = useState([]);
	useEffect(() => {
		let active = true;

		async function fetchApi() {
			//fetch the base job category info
			const response = await fetchBasicStaffList(targetUser.ID);
			const { values } = response;
			const data = processStaffDataForTable(values);
			//set the state
			setStaff(data);
			setIsLoading(false);
		}

		if (active && targetUser.ID) {
			fetchApi();
		}

		return () => {
			active = false;
		};
	}, [targetUser]);

	const renderStepContent = (step, formik) => {
		switch (step) {
			//select a single delegatee (employees-only)
			case 0:
				return (
					<SelectDelegateeField
						key={'step_' + sActiveStep}
						name={DELEGATE_FIELD_KEYS.DELEGATEE}
						formik={formik}
						directReports={sStaff}
					/>
				);
			//select one or more staff
			case 1:
				return (
					<SelectUsers
						key={'step_' + sActiveStep}
						name={DELEGATE_FIELD_KEYS.USERS_LIST}
						formik={formik}
						directReports={sStaff}
					/>
				);
			//select start and end dates
			case 2:
				return (
					<SelectDates key={'step_' + sActiveStep} formik={formik} />
				);
			//display errors or submission summary
			case 3:
				return (
					<ReviewAndSubmit
						key={'step_' + sActiveStep}
						submissionStatus={sSubmissionStatus}
					/>
				);

			default:
				return <>Default</>;
		}
	};

	return (
		<Box sx={{ width: '100%', minHeight: 200 }}>
			<>
				{messages.map(({ type, message }) => {
					return <Alert severity={type}>{message}</Alert>;
				})}
			</>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={async (values, { setSubmitting }) => {
					// setSubmitting(true);
					// await new Promise(r => setTimeout(r, 2000));
					// setSubmitting(false);
					await submitDelegation({
						...new AnnualReviewDelegationPayload(values).prepare(
							loggedInUser
						),
					})
						.then(response => {
							setSubmitting(false);
							if (response.data.payload === false) {
								let displayMessage = 'Submitted Unsuccessfully';
								if (
									response?.data?.messages &&
									response.data.messages.length > 0 &&
									response.data.messages[0].Value
								) {
									displayMessage =
										response.data.messages[0].Value;
								}
								setSubmissionStatus({
									error: true,
									submitted: false,
									message: displayMessage,
								});
							} else {
								setSubmissionStatus({
									error: false,
									submitted: true,
									message: 'Submitted Successfully',
								});
							}
						})
						.catch(() => {
							setSubmitting(false);
							setSubmissionStatus({
								error: true,
								submitted: false,
								message: 'Submission was unsuccessful',
							});
						});
					setSubmitting(true);
				}}
			>
				{formik => (
					<Form>
						<Stepper activeStep={sActiveStep} alternativeLabel>
							{stepLabels.map((label, index) => {
								const labelProps = {};
								if (
									isStepError(
										formik,
										index,
										DELEGATE_STEPS,
										DELEGATE_FIELDS
									)
								) {
									labelProps.error = true;
								}
								return (
									<Step key={'step_' + index}>
										<StepLabel {...labelProps}>
											{label}
										</StepLabel>
									</Step>
								);
							})}
						</Stepper>
						<Box sx={{ mt: 3 }}>
							<WithLoadingHandler
								isLoading={sIsLoading}
								showChildren={!sIsLoading}
							>
								{renderStepContent(sActiveStep, formik)}
							</WithLoadingHandler>
						</Box>
						<FixedBottomNavigation
							onChange={type => {
								if (type === NAVIGATION_ACTIONS.NEXT) {
									DELEGATE_FIELD_STEPS[sActiveStep].forEach(
										s => {
											if (s) {
												formik.setFieldTouched(s, true);
											}
										}
									);
								}
								onNavigate(type);
							}}
							currentStep={sActiveStep}
							stepCount={stepLabels.length}
							hasErrors={hasErrors(formik)}
							open={open}
							enableHome={false}
							enableRestart={false}
							isSubmitting={formik.isSubmitting}
							isSubmitted={sSubmissionStatus?.submitted}
						/>
					</Form>
				)}
			</Formik>
		</Box>
	);
};

DelegateForm.propTypes = {
	initialValuesFromParams: PropTypes.arrayOf(PropTypes.object),
	open: PropTypes.bool,
};

export default withQueryCheck(DelegateForm);
